import React, { useContext, useMemo } from 'react';
import { useSelector } from 'react-redux';
import PropTypes from 'prop-types';
import { ThemeContext } from 'styled-components';

import {
  getDateDetailsFromDateTimeObj,
  getTimeDetailsFromDateTimeObj,
} from 'utils/functions/dateTime';
import { staticDataInterestsMapper } from 'utils/functions/objectMapper';
import { trimText } from 'utils/functions/general';
import { rssFeedListActionsEnum } from 'modules/posts/enums';
import postsStrings from 'modules/posts/strings';
import Popup from 'modules/common/components/Popup';
import Loader from 'modules/common/components/Loader';
import SectionDivider from 'modules/common/components/SectionDivider';

import {
  Wrapper,
  GridWrapper,
  Header,
  Divider,
  ItemColumn,
  ItemColumnWithHover,
  ItemRow,
  ItemTextWrapper,
  ItemText,
  ItemClick,
  ItemHoverWrapper,
  ItemHover,
  ItemTag,
  ItemBtnWithText,
  /* ####################### */
  ActionsPopupWrapper,
  ActionsPopupTitleWrapper,
  ActionsPopupTitle,
  ActionsPopupBtnWrapper,
  ActionsPopupYesBtn,
  ActionsPopupNoBtn,
} from './RssFeedList.style';

const RssFeedActionsPopup = ({
  themeContext,
  isActionPopupLoading,
  actionPopupStatus,
  handleActionPopupConfirmBtnClick,
  handleActionPopupDismissBtnClick,
}) => (
  <Popup handleCloseBtnClick={() => handleActionPopupDismissBtnClick()}>
    <ActionsPopupWrapper>
      <ActionsPopupTitleWrapper>
        <ActionsPopupTitle>
          {
            postsStrings.rssFeedList[
              `${rssFeedListActionsEnum.getKeyByValue(actionPopupStatus.key)}${
                actionPopupStatus.value
              }ActionPopupTitle`
            ]
          }
        </ActionsPopupTitle>
      </ActionsPopupTitleWrapper>
      <SectionDivider
        gap={16}
        withSeparator
        separatorGap={16}
        borderColor={themeContext.colors.grey004}
      />
      <ActionsPopupBtnWrapper>
        {(isActionPopupLoading && <Loader width={28} height={28} thickness={4} />) || (
          <>
            <ActionsPopupYesBtn as="button" onClick={() => handleActionPopupConfirmBtnClick()}>
              {postsStrings.rssFeedList.rssFeedListActionsPopupYesBtn}
            </ActionsPopupYesBtn>
            <ActionsPopupNoBtn as="button" onClick={() => handleActionPopupDismissBtnClick()}>
              {postsStrings.rssFeedList.rssFeedListActionsPopupNoBtn}
            </ActionsPopupNoBtn>
          </>
        )}
      </ActionsPopupBtnWrapper>
    </ActionsPopupWrapper>
  </Popup>
);
RssFeedActionsPopup.propTypes = {
  themeContext: PropTypes.shape({
    colors: PropTypes.shape({
      grey004: PropTypes.string,
    }),
  }).isRequired,
  isActionPopupLoading: PropTypes.bool.isRequired,
  actionPopupStatus: PropTypes.shape({
    key: PropTypes.number,
    value: PropTypes.bool,
  }).isRequired,
  handleActionPopupConfirmBtnClick: PropTypes.func.isRequired,
  handleActionPopupDismissBtnClick: PropTypes.func.isRequired,
};

/* ############################################################################### */
/* ############################################################################### */
/* ############################################################################### */

const RssFeedList = ({
  rssFeedListArr,
  isActionPopupOpen,
  isActionPopupLoading,
  actionPopupStatus,
  handleTextToCopyToClipboardClick,
  handlePreviewFileClick,
  handleActionBtnClick,
  handleActionPopupConfirmBtnClick,
  handleActionPopupDismissBtnClick,
}) => {
  const { staticData } = useSelector(state => ({
    staticData: state.common.staticData,
  }));
  const { details: staticDetails } = staticData;

  const mappedInterests = useMemo(
    () => staticDataInterestsMapper(staticDetails.interests),
    [staticDetails],
  );

  const themeContext = useContext(ThemeContext);
  const { colors } = themeContext;

  const rssFeedGridHeaderList = postsStrings.rssFeedList.rssFeedGridHeader;

  return (
    <>
      <Wrapper>
        <GridWrapper>
          <Divider />
          {Object.keys(rssFeedGridHeaderList).map(rssFeedGridHeaderKey => (
            <Header key={rssFeedGridHeaderKey}>
              {rssFeedGridHeaderList[rssFeedGridHeaderKey]}
            </Header>
          ))}
          <Divider />
          {rssFeedListArr.map((channel, index) => {
            const {
              channelKey,
              channelLink,
              forceOpenGraphData,
              isDisabled,
              lastPullDate,
              tags,
              placeholderImage,
            } = channel;

            const lastPullDateObj = new Date(lastPullDate);
            const { day, month, year } = getDateDetailsFromDateTimeObj(lastPullDateObj);
            const { hours, minutes, period } = getTimeDetailsFromDateTimeObj(lastPullDateObj);
            const lastPullDateStr = `${day} ${month} ${year} ${hours}:${minutes} ${period}`;

            const channelLinkColor = (isDisabled && colors.redDark) || colors.greenMain;
            const channelLinkBackgroundColor = (isDisabled && colors.redLight) || colors.greenLight;

            const isLastItem = index === rssFeedListArr.length - 1;

            return (
              <React.Fragment key={channelKey}>
                <ItemColumn>
                  <ItemTextWrapper
                    backgroundColor={channelLinkBackgroundColor}
                    borderColor={channelLinkColor}
                    onClick={() => handleTextToCopyToClipboardClick(channelKey)}
                  >
                    <ItemClick color={channelLinkColor} inline>
                      {trimText(channelKey, 15)}
                    </ItemClick>
                  </ItemTextWrapper>
                </ItemColumn>
                <ItemColumnWithHover>
                  <ItemClick onClick={() => handleTextToCopyToClipboardClick(channelLink)}>
                    {trimText(channelLink, 30)}
                  </ItemClick>
                  {channelLink && (
                    <ItemHoverWrapper>
                      <ItemHover>{channelLink}</ItemHover>
                    </ItemHoverWrapper>
                  )}
                </ItemColumnWithHover>
                <ItemColumn>
                  <ItemText>{forceOpenGraphData.toString()}</ItemText>
                </ItemColumn>
                <ItemColumn>
                  <ItemText>{lastPullDateStr}</ItemText>
                </ItemColumn>
                <ItemRow>
                  <ItemTag
                    hover
                    onClick={() => handlePreviewFileClick(true, 'image', placeholderImage)}
                  >
                    {postsStrings.rssFeedList.imageFileText}
                  </ItemTag>
                </ItemRow>
                <ItemRow padding>
                  {tags.map(tag => (
                    <ItemTag key={tag} margin>
                      {mappedInterests[tag]}
                    </ItemTag>
                  ))}
                </ItemRow>
                <ItemRow>
                  <ItemBtnWithText
                    as="button"
                    color={(isDisabled && colors.greenMain) || colors.redMain}
                    backgroundColor={(isDisabled && colors.greenLight) || colors.redLight}
                    onClick={() =>
                      handleActionBtnClick(channelKey, {
                        key: rssFeedListActionsEnum.disable,
                        value: !isDisabled,
                      })
                    }
                  >
                    {(isDisabled && postsStrings.rssFeedList.enableBtnText) ||
                      postsStrings.rssFeedList.disableBtnText}
                  </ItemBtnWithText>
                  <ItemBtnWithText
                    as="button"
                    onClick={() =>
                      handleActionBtnClick(channelKey, {
                        key: rssFeedListActionsEnum.forceOpenGraph,
                        value: !forceOpenGraphData,
                      })
                    }
                  >
                    {(forceOpenGraphData && postsStrings.rssFeedList.disableOpenGraphBtnText) ||
                      postsStrings.rssFeedList.forceOpenGraphBtnText}
                  </ItemBtnWithText>
                </ItemRow>
                {isLastItem === false && <Divider />}
              </React.Fragment>
            );
          })}
        </GridWrapper>
      </Wrapper>
      {isActionPopupOpen && (
        <RssFeedActionsPopup
          themeContext={themeContext}
          isActionPopupLoading={isActionPopupLoading}
          actionPopupStatus={actionPopupStatus}
          handleActionPopupConfirmBtnClick={handleActionPopupConfirmBtnClick}
          handleActionPopupDismissBtnClick={handleActionPopupDismissBtnClick}
        />
      )}
    </>
  );
  // ###############################################################
  // <----------------------- End Return UI ----------------------->
  // ###############################################################
};

RssFeedList.propTypes = {
  rssFeedListArr: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
  isActionPopupOpen: PropTypes.bool.isRequired,
  isActionPopupLoading: PropTypes.bool.isRequired,
  actionPopupStatus: PropTypes.shape({}).isRequired,
  handleTextToCopyToClipboardClick: PropTypes.func.isRequired,
  handlePreviewFileClick: PropTypes.func.isRequired,
  handleActionBtnClick: PropTypes.func.isRequired,
  handleActionPopupConfirmBtnClick: PropTypes.func.isRequired,
  handleActionPopupDismissBtnClick: PropTypes.func.isRequired,
};

RssFeedList.defaultProps = {};

export default RssFeedList;
